import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import Link from 'redux-first-router-link';

import { CONTENT_TYPES } from '../../api';
import cssGlobals from '../../main/css/globals';
import { toPropType } from '../../main/prop-types';
import { classList } from '../../utils';
import { getHeadline, getText } from '../../utils/article';
import { getGameType } from '../../utils/productUtil';
import BigHeading from '../BigHeading';
import EmailLink from '../EmailLink';
import Image from '../Image';
import Paywall from '../Paywall';
import Pill from '../Pill';
import SmartLink from '../SmartLink';
import Text from '../Text';
import Time from '../Time';
import styles from './styles.scss';

export const articlePropType = PropTypes.shape({
  access: PropTypes.oneOf([0, 1, 2]),
  type: PropTypes.oneOf(Object.values(CONTENT_TYPES)),
  slug: PropTypes.string,
  image: PropTypes.shape({
    cdn_id: PropTypes.string.isRequired,
  }),
  video: PropTypes.shape({
    url: PropTypes.string.isRequired,
  }),
  headline: PropTypes.string.isRequired,
  publish_start: PropTypes.string,
  show_paywall: PropTypes.string,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  ),
  text: PropTypes.string,
  teaser_headline: PropTypes.string,
  teaser: PropTypes.string,
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      firstname: PropTypes.string,
      lastname: PropTypes.string,
      email: PropTypes.string,
      image: PropTypes.string,
    }).isRequired,
  ),
  round: PropTypes.shape({
    type: PropTypes.string.isRequired,
    close_time: PropTypes.string.isRequired,
  }),
});

Article.propTypes = {
  article: articlePropType.isRequired,
  onClickReadMore: PropTypes.func,
  clampLines: PropTypes.number,
  clampColor: PropTypes.string,
  to: toPropType,
  inheritColors: PropTypes.bool,
  readMoreText: PropTypes.string,
  svenskaspelUrl: toPropType,
  box: PropTypes.bool,
  showAuthorsAtBottom: PropTypes.bool,
  showSvenskaSpelLinkAtBottom: PropTypes.bool,
  showType: PropTypes.bool,
  showVideo: PropTypes.bool,
  showPublishDate: PropTypes.bool,
  showHeadline: PropTypes.bool,
  showTags: PropTypes.bool,
  imageVariant: PropTypes.oneOf(['full', 'thumbnail', 'none']),
  preferTeaser: PropTypes.bool,
  right: PropTypes.node,
  children: PropTypes.node,
};

/**
 * An article, showing the following, in order:
 *
 * 1. Optional <Pill/>, showing the type.
 * 2. Optional image.
 * 3. Optional video.
 * 4. Header (title and optional date) with an optional author image, and
 *    optional tags after the date. The <Pill/> is placed above the title if in
 *    `box` model.
 * 5. Optional HTML text.
 * 6. Optional link.
 * 7. Optional authors.
 */
export default function Article({
  article,
  clampLines,
  clampColor,
  to,
  inheritColors = false,
  readMoreText,
  svenskaspelUrl,
  box = false,
  showAuthorsAtBottom = false,
  showSvenskaSpelLinkAtBottom = false,
  showType = false,
  showVideo = true,
  showPublishDate = true,
  showHeadline = true,
  showTags = true,
  imageVariant = 'full',
  preferTeaser = false,
  right = null,
  children = null,
  onClickReadMore,
}) {
  const html = getText(article, preferTeaser);

  const headline = showHeadline
    ? getHeadline(article, preferTeaser)
    : undefined;

  const authorImageIds =
    article.authors == null
      ? []
      : article.authors.map(author => author.image_cdn).filter(Boolean);

  const titleImageId =
    showAuthorsAtBottom || authorImageIds.length === 0
      ? undefined
      : authorImageIds[0];

  const typeData =
    article.type != null && showType ? getTypeData(article) : undefined;

  const typePill =
    typeData == null ? undefined : (
      <Pill color={typeData.color}>{typeData.text}</Pill>
    );

  const svsLink =
    svenskaspelUrl == null ? undefined : (
      <div key={svenskaspelUrl} className={styles.svenskaspelLink}>
        <SmartLink to={svenskaspelUrl} colored={!inheritColors}>
          Lämna in hos Svenska Spel Sport & Casino
        </SmartLink>
      </div>
    );

  const readMoreLink =
    to == null || readMoreText == null ? undefined : (
      <SmartLink to={to} colored={!inheritColors} onClick={onClickReadMore}>
        {readMoreText}
      </SmartLink>
    );

  const subTitleContent = [
    !showPublishDate || article.publish_start == null ? undefined : (
      <Time key="date" date={new Date(article.publish_start)} />
    ),
    showTags && article.tags != null && article.tags.length > 0
      ? article.tags.map(({ name }) => (
          <Pill
            key={name}
            color={inheritColors ? 'currentColor' : cssGlobals['g-blue']}
            variant="outline"
          >
            {name}
          </Pill>
        ))
      : undefined,
    showSvenskaSpelLinkAtBottom || svsLink == null ? undefined : svsLink,
  ].filter(Boolean);

  return (
    <article className={classList({ [styles.root]: true, [styles.box]: box })}>
      {typePill != null && !box && (
        <div className={styles.typePill}>{typePill}</div>
      )}

      {imageVariant === 'full' && article.image != null && (
        <div className={styles.mediaBeforeMain}>
          <Image
            imageId={article.image.cdn_id}
            width="930"
            className={styles.fullImage}
          />
        </div>
      )}

      {showVideo && article.video != null && (
        <div className={styles.mediaBeforeMain}>
          <div className={styles.videoWrapper}>
            <ReactPlayer
              url={article.video.url}
              className={styles.video}
              width="100%"
              height="100%"
              controls="true"
            />
          </div>
        </div>
      )}

      <div className={styles.main}>
        <div className={styles.left}>
          {typePill != null && box && (
            <div className={styles.typePill}>{typePill}</div>
          )}

          <div className={styles.header}>
            {imageVariant === 'thumbnail' && article.image != null ? (
              <Image
                imageId={article.image.cdn_id}
                width="58"
                aspectRatio={[1, 1]}
                className={styles.thumbnailImage}
              />
            ) : (
              titleImageId != null && (
                <Image
                  imageId={titleImageId}
                  width="48"
                  aspectRatio={[1, 1]}
                  variant="faceSmall"
                  className={styles.titleImage}
                />
              )
            )}
            <div>
              {headline != null && (
                <BigHeading>
                  {to == null ? (
                    <span>{headline}</span>
                  ) : (
                    <Link to={to}>{headline}</Link>
                  )}
                </BigHeading>
              )}

              {subTitleContent.length > 0 && (
                <div
                  className={classList({
                    [styles.subTitle]: true,
                    [styles.greySubTitle]: !inheritColors,
                  })}
                >
                  {subTitleContent}
                </div>
              )}
            </div>
          </div>

          {html != null && (
            <div className={styles.text}>
              <Text
                html={html}
                clampLines={clampLines}
                clampColor={clampColor}
              />
            </div>
          )}

          {(readMoreLink != null ||
            (svsLink != null && showSvenskaSpelLinkAtBottom)) && (
            <div className={styles.link} aria-hidden="true">
              {showSvenskaSpelLinkAtBottom && svsLink}
              {readMoreLink != null && readMoreLink}
            </div>
          )}

          {article.authors != null &&
            article.authors.length > 0 &&
            showAuthorsAtBottom &&
            article.authors.map(author => (
              <div key={author.id} className={styles.author}>
                {author.image_cdn != null && (
                  <Image
                    imageId={author.image_cdn}
                    variant="faceSmall"
                    width="48"
                    aspectRatio={[1, 1]}
                    className={styles.authorImage}
                  />
                )}
                <div>
                  <p>{authorName(author)}</p>
                  {author.email != null && (
                    <p>
                      <EmailLink email={author.email} />
                    </p>
                  )}
                </div>
              </div>
            ))}

          {readMoreLink == null && article.show_paywall != null && (
            <div className={styles.paywall}>
              <Paywall reason={article.show_paywall} />
            </div>
          )}

          {children}
        </div>

        {right != null && <div className={styles.right}>{right}</div>}
      </div>
    </article>
  );
}

function authorName(author) {
  const name = [author.firstname, author.lastname]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name === '' ? '?' : name;
}

function getTypeData(article) {
  const { type } = article;

  switch (type) {
    case CONTENT_TYPES.DAGENS_OVERODDS:
      return {
        text: 'Spelrek',
        color: cssGlobals['g-green'],
      };

    case CONTENT_TYPES.SISTA_MINUTEN:
      return {
        text: 'Sista minuten',
        color: cssGlobals['g-grey-boba'],
      };

    case CONTENT_TYPES.ROUND_ANALYSIS: {
      const { round: { type: roundType, close_time: roundDate } = {} } =
        article;
      const gameType = getGameType(roundType);
      let label = 'Omgångsanalys';
      if (gameType != null && roundDate != null) {
        label = (
          <>
            {gameType} <Time date={new Date(roundDate)} format="EEEE d MMMM" />
          </>
        );
      }

      return {
        text: label,
        color: cssGlobals['g-green-dark'],
      };
    }

    case CONTENT_TYPES.ROUNDMATCH_ANALYSIS:
      return {
        text: 'Matchanalys Tipset',
        color: cssGlobals['g-blue-dark'],
      };

    default:
      return;
  }
}
